import { Controller } from "@hotwired/stimulus"

const OPEN_KEY = "community_chat_open"

// The Community chat as a right-hand sidebar (community/index). Show and hide
// are remembered per browser; while hidden, lines that arrive are counted onto
// the header's Chat button so a hidden chat never swallows a reply. The panel
// is only ever display:none, never removed: community-chat inside it owns the
// page's one cable subscription, and the Square hears the world through it.
//
// The header button lives outside this element, so it is found by attribute
// ([data-community-chat-toggle]) through one delegated listener rather than a
// data-action, which could never reach this controller from there. The Square
// raises "community-chat:open" when you press Enter to talk.
export default class extends Controller {
    connect() {
        this.unread = 0
        this.onClick = (event) => {
            if (!event.target.closest("[data-community-chat-toggle]")) return
            event.preventDefault()
            this.toggle()
        }
        this.onOpen = () => this.show()
        document.addEventListener("click", this.onClick)
        window.addEventListener("community-chat:open", this.onOpen)

        this.list = this.element.querySelector(".cc-scroll")
        if (this.list) {
            this.observer = new MutationObserver((records) => {
                if (!this.hidden) return
                const added = records.reduce((n, r) => n + [...r.addedNodes].filter((node) => node.nodeType === 1 && node.classList.contains("cc-msg")).length, 0)
                if (!added) return
                this.unread += added
                this.#sync()
            })
            this.observer.observe(this.list, { childList: true })
        }

        this.#apply(this.#prefersOpen(), false)
    }

    disconnect() {
        document.removeEventListener("click", this.onClick)
        window.removeEventListener("community-chat:open", this.onOpen)
        this.observer?.disconnect()
    }

    toggle() {
        this.#apply(this.hidden)
    }

    show() {
        if (this.hidden) this.#apply(true)
    }

    hide(event) {
        event?.preventDefault()
        this.#apply(false)
        // Back to the map, so the arrow keys keep walking.
        document.querySelector(".cc-square-canvas")?.focus({ preventScroll: true })
    }

    #apply(open, remember = true) {
        this.hidden = !open
        this.element.style.display = open ? "" : "none"
        document.documentElement.classList.toggle("community-chat-collapsed", !open)
        if (open) {
            this.unread = 0
            // Lines that arrived while hidden landed below the fold.
            if (this.list) this.list.scrollTop = this.list.scrollHeight
        }
        if (remember) {
            try { window.localStorage.setItem(OPEN_KEY, open ? "true" : "false") } catch (_error) { /* private mode */ }
        }
        this.#sync()
        window.dispatchEvent(new CustomEvent("jane:panels-changed"))
    }

    #prefersOpen() {
        try {
            return window.localStorage.getItem(OPEN_KEY) !== "false"
        } catch (_error) {
            return true
        }
    }

    #sync() {
        document.querySelectorAll("[data-community-chat-toggle]").forEach((button) => {
            button.classList.toggle("is-active", !this.hidden)
            button.setAttribute("aria-pressed", this.hidden ? "false" : "true")
            const badge = button.querySelector(".cc-chat-toggle-badge")
            if (!badge) return
            badge.hidden = !(this.hidden && this.unread > 0)
            badge.textContent = this.unread > 99 ? "99+" : String(this.unread)
            button.setAttribute("aria-label", this.hidden && this.unread > 0 ? `Chat, ${this.unread} new` : "Chat")
        })
    }
}
